import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CargoQuoteRequest } from '../../types/quotes';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ROUTE_ARROW } from '../../lib/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

interface ClosedOrdersListProps {
  quotes: CargoQuoteRequest[];
  loading: boolean;
  getPriceRange: (responses: CargoQuoteRequest['quote_responses']) => string;
}

export const ClosedOrdersList: React.FC<ClosedOrdersListProps> = ({
  quotes,
  loading,
  getPriceRange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="space-y-4" data-testid="closed-orders-loading">
        <Skeleton className="h-6 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
      </div>
    );
  }

  if (quotes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="closed-orders-list">
      <h2 className="text-lg">
        {t('dashboard.closed_orders.title')}
      </h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {quotes.map((quote) => (
          <Card key={quote.id} className="hover:shadow-lg transition-shadow opacity-80 hover:opacity-100">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm">
                    {quote.origin_city} {ROUTE_ARROW} {quote.destination_city}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {quote.tracking_number}
                    {' · '}
                    {formatDistanceToNow(new Date(quote.updated_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">
                  {t('dashboard.closed_orders.final_value')}
                </span>
                <span className="font-medium">
                  {quote.quote_responses.length > 0
                    ? getPriceRange(quote.quote_responses)
                    : t('common.not_available')}
                </span>
              </div>
              <Button
                onClick={() => navigate(`/dashboard/quotes/${quote.id}`)}
                variant="outline"
                size="sm"
                className="w-full"
              >
                {t('quote.list.view_details')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
